import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PlanRequestForm } from "../components/PlanRequestForm";
import { useWorkspace } from "../lib/WorkspaceContext";
import { isOpen, listPlanChangeRequests } from "../lib/billing";
import { loadEntitlements, priceFor, pricingCatalog } from "../lib/entitlements";
import type { BillingInterval, PricingCatalog } from "../lib/types";

export function PlanSelection() {
  const { active } = useWorkspace();
  const workspaceId = active?.workspace_id;
  const [params] = useSearchParams();
  // Remount the form when either the selection or workspace changes.
  return <Selection key={`${workspaceId}:${params.toString()}`} workspaceId={workspaceId} />;
}

function Selection({ workspaceId }: { workspaceId?: string }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planCode = params.get("plan") ?? "";
  const regionCode = params.get("region") ?? "";
  const interval = params.get("interval") ?? "";
  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!workspaceId) return;
    Promise.all([pricingCatalog(), loadEntitlements(workspaceId), listPlanChangeRequests(workspaceId)])
      .then(([cat, entitlements, requests]) => {
        if (cancelled) return;
        const plan = cat.plans.find((p) => p.code === planCode);
        if (!plan || !cat.regions.some((r) => r.code === regionCode)
          || !["month", "year", "lifetime"].includes(interval)
          || (plan.billing_period === "lifetime") !== (interval === "lifetime")) {
          throw new Error("Please choose a valid plan, billing period and region from Plans.");
        }
        if (planCode === (entitlements.plan?.code ?? "free")) {
          throw new Error("This workspace is already on that plan.");
        }
        if (priceFor(cat, planCode, regionCode, interval as BillingInterval)?.amount_minor === 0) {
          throw new Error("The free plan does not require a payment request.");
        }
        if (requests.some(isOpen)) {
          throw new Error("You already have a request being reviewed. Cancel it from your profile to ask for something else.");
        }
        setCatalog(cat);
      })
      .catch((e) => { if (!cancelled) setError(e.message ?? String(e)); });
    return () => { cancelled = true; };
  }, [workspaceId, planCode, regionCode, interval]);

  return (
    <section className="plan-selection">
      <Link to="/plans">← Back to plans</Link>
      <h1>Plan selection</h1>
      {error ? <div className="card err" role="alert">{error} <Link to="/profile">View your requests</Link></div>
        : !workspaceId ? <p className="muted">Select a workspace to continue.</p>
        : !catalog ? <p className="muted">Loading your selection…</p>
        : <PlanRequestForm
            workspaceId={workspaceId}
            catalog={catalog}
            planCode={planCode}
            interval={interval as BillingInterval}
            regionCode={regionCode}
            onClose={() => navigate("/plans")}
            onDone={({ request, email }) => navigate("/subscription-confirmation", {
              replace: true,
              state: { requestId: request.id, workspaceId, emailStatus: email.user },
            })}
          />}
    </section>
  );
}
